import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { LocationDetailExternalLinksConfirm } from '@/core/components/locations/location-detail-external-links-confirm';
import { LocationDetailHeaderActions } from '@/core/components/locations/location-detail-header-actions';
import { resolvePrimaryAction } from '@/core/components/locations/location-detail-overview-tab';
import {
  LocationDetailLoading,
  LocationDetailMissing,
  LocationDetailNoWorkspace,
} from '@/core/components/locations/location-detail-page-states';
import { buildLocationDetailTabs } from '@/core/components/locations/location-detail-tabs';
import { locationNeedsAttention } from '@/core/components/locations/location-health';
import { useLocationDetail } from '@/core/components/locations/use-location-detail';
import { PageHeader } from '@/core/components/page-header';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { shellPaths } from '@/core/router/app-paths';
import { AppIonPage } from '@/core/components/app-ion-page';

function LocationDetailBreadcrumb({
  domainLabel,
  locationsLabel,
}: {
  domainLabel: string;
  locationsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {locationsLabel}
    </>
  );
}

export function LocationDetailPage() {
  const backRouter = useIonBackRouter();
  const goBack = (fallback: string) => goBackOrFallback(backRouter, fallback);

  const handleBack = () => {
    goBack(shellPaths.moreWorkspaceLocations);
  };

  const detail = useLocationDetail({
    onDeleted: () => goBack(shellPaths.moreWorkspaceLocations),
  });

  const {
    dict,
    locale,
    d,
    currentWorkspace,
    location,
    isResolvingLocation,
    isError,
    refetchLocation,
    autosaveStatus,
    canManage,
    deleteMutation,
    updateFromPlaceMutation,
    clearListingMutation,
    googleConnected,
    activeTab,
    setActiveTab,
    editNameValue,
    setEditNameValue,
    flushNameSave,
    contactEmailValue,
    changeContactEmail,
    contactEmailError,
    contactPhoneValue,
    changeContactPhone,
    contactPhoneError,
    flushContactSave,
    addressDetailsValue,
    setAddressDetailsValue,
    flushAddressSave,
    latitudeValue,
    longitudeValue,
    setLatitudeValue,
    setLongitudeValue,
    handleAddressPlaceSelect,
    linkEntries,
    handleLinkEntriesChange,
    addLinkOpen,
    setAddLinkOpen,
    menuUrlError,
    setMenuUrlError,
    instagramError,
    setInstagramError,
    facebookError,
    setFacebookError,
    tiktokError,
    setTiktokError,
    linkedProducts,
    linkedProductsDict,
    locationHealth,
    showReviewInsights,
    reputationEnabled,
    reputationOverview,
    reputationOverviewLoading,
    reputationSummary,
    reputationSummaryLoading,
    reputationSummaryError,
    overviewHealthLabels,
    listHealthLabels,
    reviewInsightsLabels,
    handleOverviewNavigate,
    handleOpenReputation,
    handleOpenReputationInbox,
    openProductsForLocation,
    externalLinksConfirmOpen,
    setExternalLinksConfirmOpen,
    handleExternalLinksConfirmCancel,
    handleExternalLinksConfirmSave,
  } = detail;

  const breadcrumb = (
    <LocationDetailBreadcrumb
      domainLabel={dict.nav.navDomainWorkspace}
      locationsLabel={dict.nav.locations}
    />
  );

  if (!currentWorkspace) {
    return (
      <AppIonPage>
        <LocationDetailNoWorkspace
          dict={dict}
          d={d}
          breadcrumb={breadcrumb}
          onBack={handleBack}
        />
      </AppIonPage>
    );
  }

  if (isResolvingLocation && !location) {
    return (
      <AppIonPage>
        <LocationDetailLoading
          dict={dict}
          breadcrumb={breadcrumb}
          onBack={handleBack}
        />
      </AppIonPage>
    );
  }

  if (!location) {
    return (
      <AppIonPage>
        <LocationDetailMissing
          dict={dict}
          d={d}
          breadcrumb={breadcrumb}
          isError={isError}
          onBack={handleBack}
          onRetry={() => void refetchLocation()}
        />
      </AppIonPage>
    );
  }

  const tabs = buildLocationDetailTabs({
    d,
    locale,
    location,
    workspaceId: currentWorkspace.id,
    locationHealth,
    linkedProducts,
    linkedProductsDict,
    overviewHealthLabels,
    showReviewInsights,
    reputationOverview,
    reputationOverviewLoading,
    reputationSummary,
    reputationSummaryLoading,
    reputationSummaryError,
    reviewInsightsLabels,
    onNavigateTab: handleOverviewNavigate,
    onViewProducts: openProductsForLocation,
    onOpenReputation: handleOpenReputation,
    onOpenInbox: handleOpenReputationInbox,
    editNameValue,
    onEditNameChange: setEditNameValue,
    onEditNameBlur: flushNameSave,
    contactEmailValue,
    onContactEmailChange: changeContactEmail,
    contactEmailError,
    contactPhoneValue,
    onContactPhoneChange: changeContactPhone,
    contactPhoneError,
    onContactBlur: flushContactSave,
    addressDetails: addressDetailsValue,
    onAddressDetailsChange: setAddressDetailsValue,
    onAddressBlur: flushAddressSave,
    latitude: latitudeValue,
    longitude: longitudeValue,
    onCoordinatesChange: (lat, lng) => {
      setLatitudeValue(lat);
      setLongitudeValue(lng);
    },
    onPlaceSelect: handleAddressPlaceSelect,
    deleteMutation,
    autosaveStatus,
    canManage,
    linkEntries,
    onLinkEntriesChange: handleLinkEntriesChange,
    addLinkOpen,
    onAddLinkOpenChange: setAddLinkOpen,
    menuUrlError,
    onMenuUrlErrorChange: setMenuUrlError,
    instagramError,
    onInstagramErrorChange: setInstagramError,
    facebookError,
    onFacebookErrorChange: setFacebookError,
    tiktokError,
    onTiktokErrorChange: setTiktokError,
    googleConnected,
    updateListingMutation: updateFromPlaceMutation,
    clearListingMutation,
  });

  const headerRepairAction = locationHealth
    ? resolvePrimaryAction(
        locationHealth,
        linkedProducts.length,
        overviewHealthLabels,
        { includeReputation: reputationEnabled },
      )
    : null;
  const showHeaderTriage =
    !!locationHealth &&
    locationNeedsAttention(locationHealth, {
      includeReputation: reputationEnabled,
    });

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="overflow-x-hidden min-w-0 max-w-full md:pb-4">
          <PageHeader
            title={location.name}
            breadcrumb={breadcrumb}
            description={location.address ?? undefined}
            onBack={handleBack}
            actionsLayout="stack"
            actions={
              showHeaderTriage || headerRepairAction ? (
                <LocationDetailHeaderActions
                  locationHealth={locationHealth}
                  linkedProductCount={linkedProducts.length}
                  overviewHealthLabels={overviewHealthLabels}
                  listHealthLabels={listHealthLabels}
                  onNavigateTab={setActiveTab}
                  onViewProducts={openProductsForLocation}
                />
              ) : undefined
            }
          />
          <div className="flex flex-1 min-w-0 flex-col gap-4 md:gap-5">
            <AnimatedUnderlineTabs
              tabs={tabs}
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            />
          </div>
        </AppPageLayout>
      </AppPageContent>

      <LocationDetailExternalLinksConfirm
        open={externalLinksConfirmOpen}
        onOpenChange={setExternalLinksConfirmOpen}
        linkedProductCount={linkedProducts.length}
        dict={linkedProductsDict}
        onCancel={handleExternalLinksConfirmCancel}
        onConfirm={handleExternalLinksConfirmSave}
      />
    </AppIonPage>
  );
}
